import React, { useState } from "react";
import { createSession } from "../api/user";
import { useAuth } from "../contexts/AuthContext";
import PLANS from "../utils/plansv2";
import ContactSaleModal from "./ContactSaleModal";

const UpgradePlansModal = ({ isOpen, onClose }) => {
  const { user, organization } = useAuth();
  const [isAnnual, setIsAnnual] = useState(true);
  const [showContactSaleModal, setShowContactSaleModal] = useState(false);
  const [selectedPlanForContactSale, setSelectedPlanForContactSale] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);

  // Currency from organization (same source as PricingPlansDashboard)
  const paymentCountry = organization?.payment_country || "US";
  const isINR = paymentCountry === "IN";
  const currency = isINR ? "INR" : "USD";
  const currencySymbol = isINR ? "₹" : "$";

  const effectivePlanId = (plan) => (isINR && plan.idIN ? plan.idIN : plan.id);

  const getPrice = (plan) => {
    if (plan.contact) return null;
    const price = isINR
      ? isAnnual ? plan.priceAnnualIN : plan.priceMonthlyIN
      : isAnnual ? plan.priceAnnual : plan.priceMonthly;
    if (price == null) return null;
    return price.toLocaleString("en-US");
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const proceedRazorpaySubscription = async (subscriptionId, razorpayKeyId) => {
    const res = await loadRazorpay();
    if (!res) { alert("Razorpay SDK failed to load"); return; }
    const options = {
      key: razorpayKeyId,
      subscription_id: subscriptionId,
      name: "EzSignly",
      description: "EzSignly Subscription",
      handler: () => window.location.reload(),
      prefill: { name: user?.name, email: user?.email },
      theme: { color: "#3399cc" },
    };
    new window.Razorpay(options).open();
  };

  const handlePlanSelection = async (plan) => {
    if (plan.contact) {
      setSelectedPlanForContactSale(plan);
      setShowContactSaleModal(true);
      return;
    }
    try {
      setProcessingPlanId(plan.id);
      const response = await createSession({
        planId: effectivePlanId(plan),
        subscriptionType: isAnnual ? "YEARLY" : "MONTHLY",
        memberCount: 1,
        trialEnabled: false,
      });
      if (response.url) {
        window.location.href = response.url;
      } else if (response.paymentGateway === "RAZORPAY" && response.subscriptionId) {
        proceedRazorpaySubscription(response.subscriptionId, response.razorpayKeyId);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
    } finally {
      setProcessingPlanId(null);
    }
  };

  if (!isOpen) return null;

  const individualPlans = PLANS.filter((p) => p.section === "individual");
  const groupPlans = PLANS.filter((p) => p.section === "group");

  const PlanCard = (plan) => {
    const price = getPrice(plan);
    const isProcessing = processingPlanId === plan.id;
    return (
      <div
        key={plan.id}
        className={
          "relative bg-white border-2 rounded-xl p-5 flex flex-col transition-all hover:shadow-md " +
          (plan.highlight ? "border-primary" : "border-gray-200")
        }
      >
        {plan.ribbon && (
          <div className="absolute -top-0 rounded-t-xl left-0 right-0 bg-primary px-3 py-1 text-xs font-semibold text-white text-center">
            {plan.ribbon}
          </div>
        )}
        <div className={plan.ribbon ? "mt-5" : ""}>
          <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
          <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{plan.description}</p>
        </div>

        {/* Price */}
        <div className="mt-4 mb-4">
          {plan.contact ? (
            <span className="text-2xl font-bold text-gray-900">Let's Talk</span>
          ) : (
            <>
              <span className="text-2xl font-bold text-gray-900">
                {currencySymbol}{price}&nbsp;{currency}
              </span>
              <span className="text-sm text-gray-500 ml-1">/month</span>
            </>
          )}
          {!plan.contact && isAnnual && (
            <p className="text-xs text-green-600 mt-0.5">Billed annually · save 30%</p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-5 flex-1">
          {plan.features.map((f, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="mt-0.5 h-4 w-4 text-white bg-primary p-0.5 shrink-0 rounded-full"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              {f}
            </li>
          ))}
        </ul>

        <button
          onClick={() => handlePlanSelection(plan)}
          disabled={isProcessing}
          className={
            "w-full rounded-md px-4 py-2 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed " +
            (plan.cta.variant === "primary"
              ? "bg-primary text-white hover:bg-primary/90"
              : "border border-primary text-primary hover:bg-primary/5")
          }
        >
          {isProcessing ? "Processing…" : plan.cta.label}
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-6xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Upgrade Your Plan</h3>
            <p className="text-sm text-gray-500 mt-0.5">Select the plan that fits your needs</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-4 px-6 py-4 border-b bg-gray-50">
          <span className={!isAnnual ? "font-semibold text-gray-900" : "text-gray-400"}>Monthly</span>
          <button
            type="button"
            onClick={() => setIsAnnual((v) => !v)}
            className={
              "relative inline-flex h-6 w-11 cursor-pointer rounded-full transition-colors focus:outline-none " +
              (isAnnual ? "bg-primary" : "bg-slate-300")
            }
          >
            <span
              className={
                "pointer-events-none inline-block mt-0.5 h-5 w-5 transform rounded-full bg-white shadow transition " +
                (isAnnual ? "translate-x-5" : "translate-x-1")
              }
            />
          </button>
          <span className={isAnnual ? "font-semibold text-gray-900" : "text-gray-400"}>Annually</span>
          <span className="rounded-md border border-primary bg-primary/5 font-medium px-2 py-1 text-xs text-primary">
            Save up to 30%
          </span>
        </div>

        {/* Plans */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {/* Individual */}
          <div>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Individual Plans
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {individualPlans.map((plan) => PlanCard(plan))}
            </div>
          </div>

          {/* Group */}
          <div>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Group &amp; Multi-Company Plans
            </h4>
            <p className="text-xs text-gray-400 mb-4">
              Manage multiple companies under a single organization with shared billing.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {groupPlans.map((plan) => PlanCard(plan))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      <ContactSaleModal
        isOpen={showContactSaleModal}
        onClose={() => {
          setShowContactSaleModal(false);
          setSelectedPlanForContactSale(null);
        }}
        plan={selectedPlanForContactSale}
        user={user}
      />
    </div>
  );
};

export default UpgradePlansModal;
